// Parsing des resultats STS (sortie du moteur de tarification) en tables d'affichage
// pour la fenetre "Formules & details complementaires" (port de frmSTS.vb).
//
// Entree : la liste renvoyee par le backend getResultatsSTS, soit STS_Resultats[1..nb]
// serialise -> tableau JS 0-based d'objets { Type, FieldsCount, Fields[] }.
// ATTENTION index : l'element JS d'indice i correspond a STS_Resultats[i+1]. Le
// champ `indexResultat` des lignes vaut donc i+1 (= index 1-based attendu par le
// backend setModifSTS / enregistrement_modifsSTS).
//
// Port de : AjouterStructureDansTableau, ChargerDonnees{Patient,Facture,Formule},
// ChargerErreursTarification, ChargerChoixFormules, ChargerActes.

import { metaChamp } from './STSMetadata.js';

// --- Accesseurs defensifs (Type/type, Fields/fields, FieldsCount/fieldsCount) ---
function typeOf(r) { return Number(r && (r.Type ?? r.type)) || 0; }
function fieldsOf(r) {
    const f = r && (r.Fields ?? r.fields);
    return Array.isArray(f) ? f : [];
}
function fieldsCountOf(r) {
    const n = r && (r.FieldsCount ?? r.fieldsCount);
    return Number.isFinite(n) ? n : fieldsOf(r).length;
}
function champVal(r, i) {
    const f = fieldsOf(r);
    return i >= 0 && i < f.length ? String(f[i] ?? '') : '';
}

// Types par onglet (cf ChargerDonnees*).
const TYPES_PATIENT = [107, 108, 109, 111];
const TYPES_FACTURE_GLOBAL = [1321, 1322, 1330, 1440, 1450, 1910, 3700, 3770];
const TYPES_FACTURE_ACTE = [1610, 1620, 1630, 2010];
const TYPES_FORMULE_GLOBAL = [3720, 3725];
const TYPES_FORMULE_ACTE = [3710, 3715];
// Types dont le champ 0 (No de prestation) n'est pas affiche.
const TYPES_SANS_CHAMP0 = [1610, 1620, 1630, 2010, 3710];

// Couleurs d'erreur (cf STS_LightYellow / STS_LightRed, convertis BGR->RGB).
const COULEUR_ERREUR = {
    '1': '#ffffff',
    '2': '#ffe0c0',
    '3': '#ffc0c0',
    '4': '#ffc0c0',
    '9': '#ffc0c0',
};

// Libelle dynamique d'un champ de formule (3710/3720). Le legacy va chercher le
// vrai libelle du parametre dans la 3760 correspondante ; ici on se limite a un
// libelle generique "Parametre N" / "No de parametre N" (la structure et
// l'editabilite restent fideles).
function libelleParamFormule(type, l) {
    // l : index 0-based du champ. Param No = position dans la sequence de params.
    if (type === 3710) {
        // champs >= 4 (l >= 3) ; paires (No param, valeur)
        const noParam = Math.floor((l - 3) / 2) + 1;
        return (l % 2 === 1) ? `No de parametre ${noParam}` : `Parametre ${noParam}`;
    }
    // 3720 : champs >= 3 (l >= 2)
    const noParam = Math.floor((l - 2) / 2) + 1;
    return (l % 2 === 0) ? `No de parametre ${noParam}` : `Parametre ${noParam}`;
}

// Determine la modification ('N'|'F'|'O') d'un champ selon le 3790 suivant ou,
// a defaut, les regles propres aux 3710/3720 (cf AjouterStructureDansTableau).
function modifChamp(type, l, g3790) {
    if (g3790 && typeOf(g3790) === 3790) {
        const flag = champVal(g3790, l + 1); // 1-based dans le 3790
        const droite = flag.slice(-1);
        return (droite === 'F' || droite === 'O') ? droite : 'N';
    }
    if (type === 3710) {
        if (l <= 2) return 'N';
        return (l % 2 === 0) ? 'F' : 'N';
    }
    if (type === 3720) {
        return (l > 0) ? 'F' : 'N';
    }
    return 'N';
}

// Un champ doit-il etre cree (affiche) ? Faux si le 3790 dit "ne pas afficher"
// (gauche '' ou 'N'), ou si c'est le champ 0 d'un type lie a un acte.
function champAffiche(type, l, g3790) {
    if (l === 0 && TYPES_SANS_CHAMP0.includes(type)) return false;
    if (g3790 && typeOf(g3790) === 3790) {
        const gauche = champVal(g3790, l + 1).slice(0, 1);
        if (gauche === '' || gauche === 'N') return false;
    }
    return true;
}

function labelModif(m) {
    if (m === 'F') return 'Oui';
    if (m === 'O') return 'Oblig';
    return 'Non';
}

// Construit les lignes d'une table pour une liste de Types donnee.
// resultats : tableau JS 0-based. perActe : si true, ne garde que les groupes
// dont Fields[0] == nopresta ; sinon (global) ne garde que si nopresta <= 0.
function construireTable(resultats, types, nopresta, perActe) {
    const lignes = [];
    for (let i = 0; i < resultats.length; i++) {
        const r = resultats[i];
        const type = typeOf(r);
        if (!types.includes(type)) continue;

        if (perActe) {
            if (!(nopresta > 0 && Number(champVal(r, 0)) === Number(nopresta))) continue;
        } else if (nopresta > 0) {
            continue;
        }

        const g3790 = resultats[i + 1];
        const meta0 = metaChamp(type, 1);
        let groupePose = false;
        const nbChamps = fieldsCountOf(r);

        for (let l = 0; l < nbChamps; l++) {
            if (!champAffiche(type, l, g3790)) continue;

            let libelle;
            if ((type === 3710 && l >= 3) || (type === 3720 && l >= 2)) {
                libelle = libelleParamFormule(type, l);
            } else {
                libelle = metaChamp(type, l + 1).libelle;
            }

            const m = modifChamp(type, l, g3790);
            lignes.push({
                groupe: groupePose ? '' : meta0.nomGroupe,
                champ: libelle,
                modification: m,
                modifiable: labelModif(m),
                editable: (m === 'F' || m === 'O'),
                valeur: champVal(r, l),
                indexResultat: i + 1, // 1-based (STS_Resultats)
                noChamp: l + 1,
            });
            groupePose = true;
        }
    }
    return lignes;
}

// --- Tables par onglet ---
export function tablePatient(resultats) {
    return construireTable(resultats, TYPES_PATIENT, 0, false);
}
export function tableFacture(resultats, nopresta) {
    const g = construireTable(resultats, TYPES_FACTURE_GLOBAL, nopresta, false);
    const a = construireTable(resultats, TYPES_FACTURE_ACTE, nopresta, true);
    return g.concat(a);
}
export function tableFormules(resultats, nopresta) {
    const g = construireTable(resultats, TYPES_FORMULE_GLOBAL, nopresta, false);
    const a = construireTable(resultats, TYPES_FORMULE_ACTE, nopresta, true);
    return g.concat(a);
}

// --- Erreurs (3750) ---
export function tableErreurs(resultats, actes) {
    const lignes = [];
    for (let i = 0; i < resultats.length; i++) {
        const r = resultats[i];
        if (typeOf(r) !== 3750) continue;
        const code = champVal(r, 0);
        const gravite = code.slice(0, 1);
        const noPresta = Number(champVal(r, 2));
        let acteLabel = '';
        if (noPresta) {
            const a = (actes || []).find((x) => Number(x.noPresta) === noPresta);
            acteLabel = a ? a.label : '';
        }
        lignes.push({
            code,
            prestation: acteLabel,
            libelle: champVal(r, 1),
            couleur: COULEUR_ERREUR[gravite] || '#ffffff',
            grave: ['2', '3', '4', '9'].includes(gravite),
        });
    }
    return lignes;
}

// --- Choix de formules (3760 = disponibles ; 3710/3720 = formule choisie) ---
export function choixFormules(resultats, nopresta) {
    const options = [];
    for (let i = 0; i < resultats.length; i++) {
        const r = resultats[i];
        if (typeOf(r) !== 3760) continue;
        const cible = champVal(r, 0);
        if (cible === '' || Number(cible) === Number(nopresta)) {
            const noFormule = champVal(r, 1);
            options.push({ value: noFormule, label: `${noFormule} - ${champVal(r, 2)}` });
        }
    }
    // Formule deja choisie
    let selected = '';
    for (let i = 0; i < resultats.length; i++) {
        const r = resultats[i];
        const t = typeOf(r);
        if (t === 3710 && nopresta > 0 && Number(champVal(r, 0)) === Number(nopresta)) {
            selected = champVal(r, 1); break;
        }
        if (t === 3720 && !(nopresta > 0)) {
            selected = champVal(r, 0); break;
        }
    }
    return { options, selected };
}

// --- Liste des actes (derivee des prestations 1610) ---
// frmSTS la construit depuis facfse ; cote web on la derive des resultats 1610 :
// Fields[0]=noPresta, Fields[1]=date, Fields[3]=code prestation.
export function listeActes(resultats) {
    const actes = [{ noPresta: 0, code: '', date: '', label: '[Toutes prestations]' }];
    for (let i = 0; i < resultats.length; i++) {
        const r = resultats[i];
        if (typeOf(r) !== 1610) continue;
        const noPresta = Number(champVal(r, 0));
        const date = champVal(r, 1);
        const code = champVal(r, 3);
        if (!code) continue;
        actes.push({ noPresta, code, date, label: `${date} - ${code}` });
    }
    return actes;
}

// --- Indicateurs d'onglets (presence de donnees) ---
export function indicateurs(resultats) {
    let patient = false, facture = false, formules = false, erreurs = false, erreursGraves = false;
    for (let i = 0; i < resultats.length; i++) {
        const t = typeOf(resultats[i]);
        if (TYPES_PATIENT.includes(t)) patient = true;
        if (TYPES_FACTURE_GLOBAL.includes(t) || TYPES_FACTURE_ACTE.includes(t)) facture = true;
        if (TYPES_FORMULE_GLOBAL.includes(t) || TYPES_FORMULE_ACTE.includes(t) || t === 3760) formules = true;
        if (t === 3750) {
            erreurs = true;
            const g = champVal(resultats[i], 0).slice(0, 1);
            if (['2', '3', '4', '9'].includes(g)) erreursGraves = true;
        }
    }
    return { patient, facture, formules, erreurs, erreursGraves };
}
